import DecommissionStatus from "~/models/enums/DecommissionStatus.enum";

interface SIMPLPillProps {
  variant: DecommissionStatus | string;
  label: string;
}

interface PillStyle {
  bg: string;
  border: string;
  text: string;
  icon: "check" | "wrench" | "alert" | "person";
}

// Inline hex values — Tailwind JIT cannot scan dynamic class lookups.
const PILL_STYLES: Record<string, PillStyle> = {
  [DecommissionStatus.ACTIVATED]: {
    bg: '#E7EFFE', border: '#002B6E', text: '#002B6E', icon: "check",
  },
  [DecommissionStatus.PROVISIONING]: {
    bg: '#FBEFEF', border: '#781E1D', text: '#781E1D', icon: "wrench",
  },
  [DecommissionStatus.PRE_DECOMMISSIONING]: {
    bg: '#FBEAD1', border: '#5D3D13', text: '#5D3D13', icon: "alert",
  },
  [DecommissionStatus.DECOMMISSIONED]: {
    bg: '#FBEFEF', border: '#781E1D', text: '#781E1D', icon: "alert",
  },
  [DecommissionStatus.PENDING_CREDENTIALS]: {
    bg: '#FBEAD1', border: '#5D3D13', text: '#5D3D13', icon: "wrench",
  },
  [DecommissionStatus.RENEWAL_CREDENTIALS]: {
    bg: '#E7EFFE', border: '#002B6E', text: '#002B6E', icon: "check",
  },
  [DecommissionStatus.AWAITING_PROVISIONING_INPUT]: {
    bg: '#FBEAD1', border: '#5D3D13', text: '#5D3D13', icon: "person",
  },
  [DecommissionStatus.AWAITING_RENEWAL_INPUT]: {
    bg: '#FBEAD1', border: '#5D3D13', text: '#5D3D13', icon: "person",
  },
  [DecommissionStatus.READY_TO_SEND]: {
    bg: '#FBEAD1', border: '#5D3D13', text: '#5D3D13', icon: "person",
  },
};

const FALLBACK: PillStyle = {
  bg: '#E7EFFE', border: '#002B6E', text: '#002B6E', icon: "check",
};

// Figma icons from eui-chip/icon/ion/filled — inlined as SVG
const IconCheck = ({ color }: { color: string }) => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.75 0C4.37391 0 0 4.37391 0 9.75C0 15.1261 4.37391 19.5 9.75 19.5C15.1261 19.5 19.5 15.1261 19.5 9.75C19.5 4.37391 15.1261 0 9.75 0ZM14.8242 6.48234L8.52422 13.9823C8.45511 14.0647 8.36912 14.1312 8.27207 14.1773C8.17502 14.2235 8.06918 14.2483 7.96172 14.25H7.94906C7.84394 14.25 7.74 14.2278 7.64398 14.185C7.54797 14.1422 7.46202 14.0797 7.39172 14.0016L4.69172 11.0016C4.62315 10.9288 4.56981 10.8431 4.53483 10.7495C4.49986 10.6559 4.48395 10.5562 4.48805 10.4563C4.49215 10.3564 4.51617 10.2584 4.5587 10.1679C4.60124 10.0775 4.66142 9.99643 4.73572 9.92957C4.81002 9.8627 4.89694 9.81137 4.99136 9.77857C5.08579 9.74578 5.18581 9.73219 5.28556 9.73861C5.38531 9.74502 5.48277 9.77131 5.57222 9.81594C5.66166 9.86056 5.74128 9.9226 5.80641 9.99844L7.92938 12.3572L13.6758 5.51766C13.8047 5.36863 13.987 5.27631 14.1835 5.26066C14.3799 5.24501 14.5746 5.30728 14.7254 5.43402C14.8763 5.56075 14.9712 5.74176 14.9897 5.93793C15.0082 6.1341 14.9488 6.32966 14.8242 6.48234Z" fill={color}/>
  </svg>
);

const IconWrench = ({ color }: { color: string }) => (
  <svg width="14" height="14" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20.5101 4.18455C20.4561 4.07951 20.3781 3.98872 20.2823 3.91955C20.1866 3.85039 20.0759 3.80482 19.9592 3.78654C19.8425 3.76827 19.7231 3.77781 19.6108 3.81439C19.4985 3.85097 19.3965 3.91355 19.3129 3.99705L16.4329 6.87892C16.2916 7.01825 16.101 7.09635 15.9025 7.09635C15.704 7.09635 15.5135 7.01825 15.3721 6.87892L14.1271 5.63205C13.9857 5.49272 13.9076 5.30219 13.9076 5.10369C13.9076 4.90519 13.9857 4.71466 14.1271 4.57533L16.995 1.70299C17.0809 1.61703 17.1447 1.51146 17.1808 1.39537C17.2169 1.27928 17.2243 1.15616 17.2022 1.0366C17.1802 0.917045 17.1294 0.80464 17.0543 0.709069C16.9791 0.613497 16.8819 0.537624 16.7709 0.487985C14.6048 -0.480452 11.9011 0.0248601 10.1962 1.71705C8.74777 3.15517 8.31605 5.40236 9.01262 7.88252C9.05017 8.01478 9.05039 8.15485 9.01324 8.28722C8.97609 8.4196 8.90303 8.53911 8.80215 8.63252L0.998399 15.7599C0.398399 16.2999 0.0483994 17.0699 0.00109603 17.9169C-0.00993207 18.3253 0.0623597 18.7317 0.213562 19.1112C0.364764 19.4908 0.591706 19.8356 0.880544 20.1245C1.16938 20.4135 1.51406 20.6406 1.89356 20.792C2.27305 20.9433 2.67941 21.0158 3.08783 21.005C3.49626 20.9941 3.89819 20.9002 4.26912 20.7289C4.64004 20.5576 4.97218 20.3125 5.24527 20.0086L12.449 12.1875C12.5411 12.0879 12.6586 12.0154 12.7888 11.9776C12.9191 11.9399 13.0572 11.9384 13.1882 11.9733C15.8943 12.7668 18.2943 11.7652 19.2853 10.7888C21.1204 8.9808 21.4003 5.91424 20.5101 4.18455Z" fill={color}/>
  </svg>
);

const IconAlert = ({ color }: { color: string }) => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.75 0C4.37391 0 0 4.37391 0 9.75C0 15.1261 4.37391 19.5 9.75 19.5C15.1261 19.5 19.5 15.1261 19.5 9.75C19.5 4.37391 15.1261 0 9.75 0ZM9.75 14.9958C9.56458 14.9958 9.38332 14.9408 9.22915 14.8378C9.07498 14.7348 8.95482 14.5884 8.88386 14.417C8.81291 14.2457 8.79434 14.0572 8.83051 13.8754C8.86669 13.6935 8.95598 13.5265 9.08709 13.3954C9.2182 13.2643 9.38525 13.175 9.5671 13.1388C9.74896 13.1026 9.93746 13.1212 10.1088 13.1921C10.2801 13.2631 10.4265 13.3833 10.5295 13.5374C10.6325 13.6916 10.6875 13.8729 10.6875 14.0583C10.6875 14.3069 10.5887 14.5454 10.4129 14.7212C10.2371 14.897 9.99864 14.9958 9.75 14.9958ZM10.7681 5.56687L10.4991 11.2856C10.4991 11.4845 10.42 11.6753 10.2794 11.816C10.1387 11.9566 9.94797 12.0356 9.74906 12.0356C9.55015 12.0356 9.35938 11.9566 9.21873 11.816C9.07808 11.6753 8.99906 11.4845 8.99906 11.2856L8.73 5.56969C8.7241 5.42997 8.74607 5.29282 8.79458 5.16415C8.84308 5.03549 8.91713 4.91797 9.01225 4.81868C9.10737 4.71938 9.22161 4.64037 9.34807 4.58639C9.47454 4.53241 9.61062 4.50458 9.74813 4.50458C9.88563 4.50458 10.0217 4.53241 10.1482 4.58639C10.2746 4.64037 10.3889 4.71938 10.484 4.81868C10.5791 4.91797 10.6532 5.03549 10.7017 5.16415C10.7502 5.29282 10.7721 5.42997 10.7662 5.56734L10.7681 5.56687Z" fill={color}/>
  </svg>
);

const IconPerson = ({ color }: { color: string }) => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.75 0C4.37391 0 0 4.37391 0 9.75C0 15.1261 4.37391 19.5 9.75 19.5C15.1261 19.5 19.5 15.1261 19.5 9.75C19.5 4.37391 15.1261 0 9.75 0ZM7.39594 5.47594C7.98984 4.84641 8.82563 4.5 9.75 4.5C10.6744 4.5 11.5027 4.84875 12.0989 5.48156C12.7031 6.12281 12.997 6.98437 12.9277 7.91062C12.7889 9.75 11.3639 11.25 9.75 11.25C8.13609 11.25 6.70828 9.75 6.57234 7.91016C6.50344 6.97641 6.79688 6.11203 7.39594 5.47594ZM9.75 18C8.64866 18.0007 7.55838 17.7803 6.54382 17.3518C5.52927 16.9232 4.61109 16.2953 3.84375 15.5053C4.28323 14.8786 4.8432 14.3457 5.49094 13.9378C6.68578 13.1719 8.19797 12.75 9.75 12.75C11.302 12.75 12.8142 13.1719 14.0077 13.9378C14.6559 14.3455 15.2164 14.8784 15.6562 15.5053C14.889 16.2954 13.9708 16.9234 12.9562 17.3519C11.9417 17.7804 10.8514 18.0008 9.75 18Z" fill={color}/>
  </svg>
);

const ICON_MAP = { check: IconCheck, wrench: IconWrench, alert: IconAlert, person: IconPerson };

export const SIMPLPill = ({ variant, label }: SIMPLPillProps) => {
  const { bg, border, text, icon } = PILL_STYLES[variant] ?? FALLBACK;
  const Icon = ICON_MAP[icon];

  return (
    <span
      style={{
        backgroundColor: bg,
        border: `1px solid ${border}`,
        color: text,
        borderRadius: '9999px',
        padding: '4px 12px',
        minHeight: '32px',
        minWidth: '64px',
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '24px',
        whiteSpace: 'nowrap',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
      }}
    >
      <Icon color={text} />
      {label}
    </span>
  );
};

export default SIMPLPill;
